import { createSlice } from '@reduxjs/toolkit';
import { setToken, removeToken } from '../../utils/localStorage';

const initialState = {
  user: null,
  token: null,
  userStats: null,
  isAuthenticated: false,
  rememberMe: false,
  loading: false,
  resendLoading: false,
  error: null,
  googleConnectionStatus: null,
};

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to store ${key} data:`, error);
  }
};

const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to delete ${key} data:`, error);
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = action.payload.user || null;
      state.rememberMe = action.payload.rememberMe || false;

      if (action.payload.token) {
        setToken(action.payload.token);
      }

      if (action.payload.user) {
        safeSetItem('user', action.payload.user);
      }

      if (action.payload.rememberMe) {
        safeSetItem('auth', {
          token: action.payload.token,
          rememberMe: action.payload.rememberMe,
        });
      } else {
        safeRemoveItem('auth');
      }
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    signUpStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    signUpSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    signUpFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    otpVerifyStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    otpVerifySuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    otpVerifyFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    resendOTPStart: (state) => {
      state.resendLoading = true;
    },
    resendOTPSuccess: (state) => {
      state.resendLoading = false;
    },
    resendOTPFailure: (state) => {
      state.resendLoading = false;
    },
    forgotPasswordStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    forgotPasswordSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    forgotPasswordFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    resetPasswordStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    resetPasswordSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    resetPasswordFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    stopLoading: (state) => {
      state.loading = false;
    },
    setUser: (state, action) => {
      state.user = action.payload?.profile;
      state.userStats = action.payload?.stats || null;
      if (action.payload?.profile) {
        safeSetItem('user', action.payload.profile);
      }
    },
    loadStoredData: (state, action) => {
      const { authData, userData } = action.payload;

      if (authData) {
        state.token = authData.token;
        state.rememberMe = authData.rememberMe;
        state.isAuthenticated = !!authData.token;
        if (authData.token) {
          setToken(authData.token);
        }
      }
      if (userData) {
        state.user = userData;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.userStats = null;
      state.isAuthenticated = false;
      state.rememberMe = false;
      state.loading = false;
      state.error = null;
      state.googleConnectionStatus = null;
      removeToken();
      safeRemoveItem('auth');
      safeRemoveItem('user');
      safeRemoveItem('token');
      safeRemoveItem('scripts');
      safeRemoveItem('libraryTopics');
    },
    updateUserProfile: (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        };
      } else {
        state.user = action.payload;
      }
      if (state.user) {
        safeSetItem('user', state.user);
      }
    },
    setGoogleConnectionStatus: (state, action) => {
      state.googleConnectionStatus = action.payload;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  signUpStart,
  signUpSuccess,
  signUpFailure,
  otpVerifyStart,
  otpVerifySuccess,
  otpVerifyFailure,
  resendOTPStart,
  resendOTPSuccess,
  resendOTPFailure,
  forgotPasswordStart,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordStart,
  resetPasswordSuccess,
  resetPasswordFailure,
  setUser,
  logout,
  stopLoading,
  loadStoredData,
  clearError,
  updateUserProfile,
  setGoogleConnectionStatus,
} = authSlice.actions;

export default authSlice.reducer;
